// Auth slice — participant identity + the login picker list (the dummy-login auth
// seam, INTEGRATION.md §4). Identity is sent as the X-User-Id header (persisted to
// localStorage by the caller); these thunks own the only `/me` and `/users` calls.
// `useAuth()` (auth/AuthProvider) wraps this slice so components keep the same API.
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiGet } from '../../services/api/client';
import { STORAGE_KEYS } from '../../config/constants';

const readStored = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

// Shared /me bootstrap: identity + whichever collection the persona owns, with a
// default selected account: the Relius handoff's plan when there is one, else the
// persisted one, else the first account.
//
// `/me` returns `role` plus BOTH collections, one of them empty: a participant gets
// `accounts` (their stake in each plan), a sponsor gets `plans` (the plans they
// administer). A sponsor therefore has no selectedAccountId — the sponsor screens are
// keyed by planId from the route, not by a selected account.
const fetchMe = async (preferredAccountId) => {
  // { participantId, firstName, lastName, fullName?, role, accounts[], plans[], session? }
  // `session` is the Relius context ({ sessionId, userId, planId }) the embedding parent
  // handed over; present in embedded mode only, so it is normalised to null rather than
  // left undefined.
  const me = await apiGet('/me');
  const accts = me.accounts || [];
  // From the Relius portal, ONLY the plan the handoff names — never another account.
  const handoffPlanId = me.session?.planId;
  const chosen = handoffPlanId
    ? accts.find((a) => a.planId === handoffPlanId)?.accountId ?? null
    : accts.find((a) => a.accountId === preferredAccountId)?.accountId || accts[0]?.accountId || null;
  return {
    participant: { participantId: me.participantId, firstName: me.firstName, lastName: me.lastName, fullName: me.fullName ?? null },
    // Absent role means the pre-sponsor contract, i.e. a participant.
    role: me.role || 'participant',
    accounts: accts,
    sponsorPlans: me.plans || [],
    selectedAccountId: chosen,
    session: me.session
      ? { sessionId: me.session.sessionId ?? null, userId: me.session.userId ?? null, planId: me.session.planId ?? null }
      : null,
  };
};

// Log in as a participant. The caller persists USER_ID first so the client sends
// the X-User-Id header on /me.
export const loginUser = createAsyncThunk('auth/login', async (userId, { rejectWithValue }) => {
  try {
    return { userId, ...(await fetchMe(readStored(STORAGE_KEYS.ACCOUNT_ID))) };
  } catch (err) {
    // Keep the backend's `code` and `status` alongside the message: the embedded login
    // renders a dedicated page for a session with no participant record (by code) and
    // tells a 401 apart from any other failure (by status) — on the /me leg as much as
    // on /sso/accept.
    return rejectWithValue({ message: err.message || 'Could not load your profile.', code: err.code, status: err.status });
  }
});

// Re-establish a persisted session on app start (skipped when there's no userId).
export const bootstrapAuth = createAsyncThunk(
  'auth/bootstrap',
  async (_, { rejectWithValue }) => {
    try {
      return { userId: readStored(STORAGE_KEYS.USER_ID), ...(await fetchMe(readStored(STORAGE_KEYS.ACCOUNT_ID))) };
    } catch (err) {
      return rejectWithValue(err.message || 'Session expired.');
    }
  },
  // Run only with a persisted session, and only once. `status` can't gate this
  // (initial state is already 'loading' when a session exists — see initialState),
  // so a dedicated `bootstrapStarted` flag blocks the React StrictMode double-fire
  // (AuthProvider's mount effect runs twice in dev → two /me calls). See FRONTEND_RULES §7.
  {
    condition: (_, { getState }) => !!readStored(STORAGE_KEYS.USER_ID) && !getState().auth.bootstrapStarted,
  },
);

// The sample participants for the login picker.
export const fetchUsers = createAsyncThunk(
  'auth/fetchUsers',
  async (_, { rejectWithValue }) => {
    try {
      return await apiGet('/users');
    } catch (err) {
      return rejectWithValue(err.message || 'Could not load the sign-in list.');
    }
  },
  // Single-fetch guard: the picker list takes no arg and only needs loading once.
  // Skip while a request is in flight or already loaded — collapses the StrictMode
  // double-fire on the Login screen into one /users call. A retry after failure
  // still runs (status is 'failed', not 'loading'/'succeeded'). See FRONTEND_RULES §7.
  { condition: (_, { getState }) => !['loading', 'succeeded'].includes(getState().auth.usersStatus) },
);

const hasSession = !!readStored(STORAGE_KEYS.USER_ID);
const initialState = {
  userId: readStored(STORAGE_KEYS.USER_ID),
  participant: null,
  // Which persona is signed in. Defaults to 'participant' so nothing reads as a
  // sponsor before /me has answered (RequireRole holds on `status === 'loading'`).
  role: 'participant',
  accounts: [],
  sponsorPlans: [], // populated for a sponsor only — the plans they administer
  selectedAccountId: readStored(STORAGE_KEYS.ACCOUNT_ID),
  session: null, // Relius context from /me (embedded mode): { sessionId, userId, planId }
  status: hasSession ? 'loading' : 'idle', // 'idle' | 'loading' | 'authenticated' | 'error'
  error: null,
  bootstrapStarted: false, // set once bootstrapAuth fires — single-fetch guard for /me
  users: [],
  usersStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  usersError: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setSelectedAccount(state, action) {
      state.selectedAccountId = action.payload;
    },
    clearAuth(state) {
      state.userId = null;
      state.participant = null;
      state.role = 'participant';
      state.accounts = [];
      state.sponsorPlans = [];
      state.selectedAccountId = null;
      state.session = null;
      state.status = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    const onPending = (state) => { state.status = 'loading'; state.error = null; };
    const onAuthed = (state, action) => {
      state.status = 'authenticated';
      // Identity used for participant-keyed calls (/overview, /notifications) is the
      // participantId that /me authoritatively returns — NOT the login arg, which in
      // OIDC mode is the IdP subject (a GUID). Dummy login passes the participantId,
      // so this is a no-op there.
      state.userId = action.payload.participant?.participantId || action.payload.userId;
      state.participant = action.payload.participant;
      state.role = action.payload.role || 'participant';
      state.accounts = action.payload.accounts;
      state.sponsorPlans = action.payload.sponsorPlans || [];
      state.selectedAccountId = action.payload.selectedAccountId;
      state.session = action.payload.session ?? null;
    };
    builder
      .addCase(loginUser.pending, onPending)
      .addCase(loginUser.fulfilled, onAuthed)
      .addCase(loginUser.rejected, (state, action) => { state.status = 'error'; state.error = action.payload?.message || 'Sign-in failed'; })
      .addCase(bootstrapAuth.pending, (state) => { onPending(state); state.bootstrapStarted = true; })
      .addCase(bootstrapAuth.fulfilled, onAuthed)
      // A bad/stale persisted session drops to logged-out (RequireAuth → /login).
      .addCase(bootstrapAuth.rejected, (state, action) => { state.status = 'idle'; state.userId = null; state.error = action.payload || null; })
      .addCase(fetchUsers.pending, (state) => { state.usersStatus = 'loading'; state.usersError = null; })
      .addCase(fetchUsers.fulfilled, (state, action) => { state.usersStatus = 'succeeded'; state.users = action.payload; })
      .addCase(fetchUsers.rejected, (state, action) => { state.usersStatus = 'failed'; state.usersError = action.payload || 'Failed to load participants'; });
  },
});

export const { setSelectedAccount, clearAuth } = authSlice.actions;

export const selectAuth = (state) => state.auth;

export default authSlice.reducer;
